import styled from "styled-components"
import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import { ASC, PUBLIC, NON_PUBLIC, PRIVATE } from "const"
import { PrivatePublicNonPublic, deepCopy } from "client/utils"
import {
    StoreType,
    PackageList,
    SortAccounts,
    setPackageList,
    setPackageAllowed,
    setPackageActiveCell,
} from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmDescription,
    FormGroup,
    Label,
    RadioButton,
    Logo,
    AccountDropdown,
    AccountDropdownList,
    Feedback,

    TablePopoverData,
    TablePopover,
} from "client/components"


const TableSelectParentStyled = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 8px 12px;
    display: flex;
    align-items: center;

    & > div > div { // styled "Confirm"
        padding-bottom: 0;
        min-height: unset;

        & > div {
            overflow: visible;
        }
    }
`
const TableVisibilityTitle = styled.div`
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`
const TableAllowedAccount = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 2px 0;
`
const TableAllowedAccountName = styled.span`
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: 0 12px;
`


interface AllowedAccountsProps {
    packageId: number
}
export const TableAllowedAccounts = ({
    packageId,
}: AllowedAccountsProps) => {
    const accountList = useSelector((state: StoreType) => state.account.accountList)
    const packageAllowed = useSelector((state: StoreType) => state.package.packageAllowed)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [accountIds, setAccountIds] = useState<number[]>([])

    useEffect(() => {
        const sortedIds = SortAccounts(Object.values(accountList), "name", ASC, {})
        const allowedAccountIds = packageAllowed[packageId] || []
        setAccountIds(sortedIds.filter((accountId) => allowedAccountIds.includes(accountId)))
    }, [accountList, packageAllowed, packageId])

    const dataList: TablePopoverData[] = []
    accountIds.forEach((accountId) => {
        const account = accountList[accountId]
        if (account) {
            dataList.push({
                id: `${accountId}_${packageId}`,
                title: (
                    <TableAllowedAccount>
                        <Logo
                            logo={account.logo || Images.accountLogo}
                            size={30}
                            borderRadius={4}
                        />
                        <TableAllowedAccountName>{account.name}</TableAllowedAccountName>
                    </TableAllowedAccount>
                ),
            })
        }
    })

    return (
        <TablePopover
            id={`allowedAccounts${packageId}`}
            dataList={dataList}
            placement="left"
            listStyle={false}
            title={lang.accounts}
            showFirst={dataList.length === 1}
            editAccess={false}
        />
    )
}


export const TableVisibility = () => {
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const packageAllowed = useSelector((state: StoreType) => state.package.packageAllowed)
    const activeCell = useSelector((state: StoreType) => state.package.activeCell)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [visibility, setVisibility] = useState<PrivatePublicNonPublic>(null)
    const [allowedAccounts, setAllowedAccounts] = useState<number[]>([])

    useEffect(() => {
        const { packageId } = activeCell
        setVisibility(packageList[packageId] ? packageList[packageId].visibility : null)
        setAllowedAccounts(packageAllowed[packageId] as number[] || [])
    }, [activeCell, packageList, packageAllowed])

    const onChange = () => {
        if ((visibility === NON_PUBLIC || visibility === PRIVATE) && !allowedAccounts.length) {
            dispatch(setPackageActiveCell(null))
            return
        }

        const packageListCopy: PackageList = packageList ? deepCopy(packageList) : {}
        const { packageId } = activeCell
        const packg = deepCopy(packageListCopy[packageId])

        packageListCopy[packageId] = {
            ...packg,
            visibility,
        }
        dispatch(setPackageList(packageListCopy))
        dispatch(setPackageAllowed({ [packageId]: allowedAccounts }))
        dispatch(setPackageActiveCell(null))
    }

    const activePackage = packageList[activeCell.packageId]

    return (
        <TableSelectParentStyled>
            {
                activeCell.column === "visibility"
                && <TableVisibilityTitle>{lang[activePackage.visibility]}</TableVisibilityTitle>
            }
            {
                activeCell.column === "allowedAccounts"
                && <TableAllowedAccounts packageId={activeCell.packageId} />
            }

            <ConfirmWrapper show onClose={onChange}>
                <Confirm>
                    <ConfirmDescription>
                        <FormGroup>
                            <Label>{lang.visibility}</Label>
                            <FormGroup>
                                <RadioButton
                                    checked={visibility === PUBLIC}
                                    onChange={() => {
                                        setVisibility(PUBLIC)
                                        setAllowedAccounts([])
                                    }}
                                    title={lang.public}
                                />
                                <RadioButton
                                    checked={visibility === NON_PUBLIC}
                                    onChange={() => {
                                        setVisibility(NON_PUBLIC)
                                        setAllowedAccounts([])
                                    }}
                                    title={lang.nonPublic}
                                />
                                <RadioButton
                                    checked={visibility === PRIVATE}
                                    onChange={() => {
                                        setVisibility(PRIVATE)
                                        setAllowedAccounts([])
                                    }}
                                    title={lang.private}
                                />
                            </FormGroup>
                        </FormGroup>

                        {
                            visibility === NON_PUBLIC && (
                                <FormGroup>
                                    <Label>{lang.accounts}</Label>
                                    <AccountDropdownList
                                        selectedIds={allowedAccounts}
                                        title={`+ ${lang.addAccount}`}
                                        onChange={setAllowedAccounts}
                                    />
                                    <Feedback>{!allowedAccounts.length && lang.validationFieldRequired}</Feedback>
                                </FormGroup>
                            )
                        }
                        {
                            visibility === PRIVATE && (
                                <FormGroup>
                                    <Label>{lang.account}</Label>
                                    <AccountDropdown
                                        selectedId={allowedAccounts.length ? allowedAccounts[0] : null}
                                        title={lang.selectAccount}
                                        showDelete={false}
                                        onChange={(accountId) => setAllowedAccounts([accountId])}
                                    />
                                    <Feedback>{!allowedAccounts.length && lang.validationFieldRequired}</Feedback>
                                </FormGroup>
                            )
                        }
                    </ConfirmDescription>
                </Confirm>
            </ConfirmWrapper>
        </TableSelectParentStyled>
    )
}
